import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Col, Form, Row } from 'react-bootstrap';

import AppLayout from '../../../layouts/AppLayout/AppLayout';
import { NioButton, NioCard, NioField, NioSection } from '../../../components';
import api, { mergeStoredUser } from '../../../utils/api';
import { useTranslation } from '../../../i18n/I18nProvider';

function getStoredUser() {
  try {
    const raw = localStorage.getItem('user');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function buildProviderForm(u) {
  return {
    phone: u?.phone || '',
    region: u?.region || '',
    city: u?.city || '',
    address: u?.address || '',
    businessName: u?.businessName || '',
    sector: u?.sector || '',
    subSector: u?.subSector || '',
    experience: u?.experience || '',
    qualifications: u?.qualifications || '',
    description: u?.description || '',
    about: u?.about || '',
    clientCommitments: u?.clientCommitments || '',
    paymentMethod: u?.paymentMethod || '',
    coverageGuarantees: u?.coverageGuarantees || '',
    pricing: u?.pricing || '',
    schedule: u?.schedule || '',
    linkedin: u?.linkedin || '',
    facebook: u?.facebook || '',
    twitter: u?.twitter || '',
    instagram: u?.instagram || '',
    website: u?.website || '',
  };
}

const tunisianRegions = {
  Ariana: ['Ariana', 'Raoued', 'Kalâat el-Andalous', 'Sidi Thabet', 'Ettadhamen', 'Mnihla'],
  'Béja': ['Béja', 'Medjez el-Bab', 'Testour', 'Nefza', 'Amdoun', 'Téboursouk'],
  'Ben Arous': ['Ben Arous', 'Rades', 'Mégrine', 'Hammam Lif', 'Fouchana', 'Ezzahra', 'Mohamedia'],
  Bizerte: ['Bizerte', 'Menzel Bourguiba', 'Mateur', 'Sejnane', 'Ghar El Melh', 'Ras Jebel'],
  'Gabès': ['Gabès', 'Médenine', 'Matmata', 'El Hamma', 'Ghannouch', 'Mareth'],
  Gafsa: ['Gafsa', 'El Ksar', 'Métlaoui', 'Redeyef', 'Moularès', 'Sidi Aïch'],
  Jendouba: ['Jendouba', 'Tabarka', 'Aïn Draham', 'Fernana', 'Bou Salem', 'Ghardimaou'],
  Kairouan: ['Kairouan', 'Sbikha', 'Haffouz', 'Chebika', 'Oueslatia', 'Nasrallah'],
  Kasserine: ['Kasserine', 'Sbeitla', 'Fériana', 'Thala', 'Haïdra', 'Jedelienne'],
  'Kébili': ['Kébili', 'Douz', 'Souk Lahad', 'Faouar', 'El Golâa'],
  Kef: ['Le Kef', 'Tajerouine', 'Kalâat Khasba', 'Dahmani', 'Sers', 'Nebeur'],
  Mahdia: ['Mahdia', 'Bou Merdes', 'Chebba', 'El Jem', 'Ksour Essef', 'Sidi Alouane'],
  Manouba: ['Manouba', 'Douar Hicher', 'Oued Ellil', 'Mornag', 'Borj El Amri', 'Djedeida'],
  'Médenine': ['Médenine', 'Tataouine', 'Ben Gardane', 'Zarzis', 'Djerba', 'Houmt Souk'],
  Monastir: ['Monastir', 'Moknine', 'Ksibet el-Médiouni', 'Bembla', 'Téboulba', 'Sayada'],
  Nabeul: ['Nabeul', 'Hammamet', 'Kélibia', 'Korba', 'Menzel Temime', 'Dar Chaabane', 'Béni Khalled'],
  Sfax: ['Sfax', 'Sakiet Ezzit', 'El Hencha', 'Agareb', 'Menzel Chaker', 'Bir Ali Ben Khalifa'],
  'Sidi Bouzid': ['Sidi Bouzid', 'Meknassy', 'Regueb', 'Ouled Haffouz', 'Cebbala Ouled Asker'],
  Siliana: ['Siliana', 'Bou Arada', 'Gaâfour', 'El Krib', 'Makthar', 'Rouhia'],
  Sousse: ['Sousse', 'Hammam Sousse', 'Akouda', 'Enfidha', 'Kalaa Kebira', 'Msaken', 'Bouficha'],
  Tataouine: ['Tataouine', 'Remada', 'Bir Lahmar', 'Ghomrassen', 'Dehiba'],
  Tozeur: ['Tozeur', 'Nefta', 'Degache', 'Hazoua', 'Tamaghza'],
  Tunis: ['Tunis', 'La Marsa', 'Carthage', 'Sidi Bou Said', 'Le Bardo', 'La Goulette'],
  Zaghouan: ['Zaghouan', 'Zriba', 'Nadhour', 'Fahs', 'Saouaf', 'El Fahs'],
};

const sortedRegions = Object.keys(tunisianRegions).sort();

export default function ProviderSettings() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [user, setUser] = useState(getStoredUser);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [providerForm, setProviderForm] = useState(buildProviderForm(getStoredUser()));

  const [portfolioPhotoFiles, setPortfolioPhotoFiles] = useState([]);
  const [portfolioVideoFiles, setPortfolioVideoFiles] = useState([]);
  const [portfolioProjectFiles, setPortfolioProjectFiles] = useState([]);

  const [serviceId, setServiceId] = useState('');
  const [skills, setSkills] = useState([]);
  const [skillInput, setSkillInput] = useState('');

  const availableProviderCities = useMemo(() => {
    if (!providerForm?.region || !tunisianRegions[providerForm.region]) return [];
    return [...tunisianRegions[providerForm.region]].sort();
  }, [providerForm?.region]);

  const sectorOptions = useMemo(() => {
    return (services || [])
      .map((s) => String(s?.title || '').trim())
      .filter(Boolean);
  }, [services]);

  const subSectorOptions = useMemo(() => {
    const selectedSector = String(providerForm?.sector || '').trim().toLowerCase();
    if (!selectedSector) return [];
    const matchedService = (services || []).find(
      (s) => String(s?.title || '').trim().toLowerCase() === selectedSector
    );
    if (!matchedService) return [];
    if (Array.isArray(matchedService.categories) && matchedService.categories.length) {
      return matchedService.categories.filter(Boolean);
    }
    if (Array.isArray(matchedService.tags) && matchedService.tags.length) {
      return matchedService.tags.filter(Boolean);
    }
    return [];
  }, [providerForm?.sector, services]);

  useEffect(() => {
    const u = getStoredUser();
    if (!u) {
      navigate('/auth/login', { replace: true });
      return;
    }
    if (u.role !== 'provider') {
      navigate('/profile', { replace: true });
      return;
    }
    setUser(u);
    setServiceId(u.serviceId || '');
    setSkills(Array.isArray(u.skills) ? u.skills : []);
    setProviderForm(buildProviderForm(u));
    setLoading(true);
    Promise.allSettled([api.getServices(), api.getProfile(u.id || u._id)])
      .then(async ([servicesResult, profileResult]) => {
        if (servicesResult.status === 'fulfilled') {
          const res = servicesResult.value;
          if (res && res.success && Array.isArray(res.data)) setServices(res.data);
        }

        if (profileResult.status === 'fulfilled') {
          const profileData = profileResult.value;
          if (profileData && profileData.success && profileData.data) {
            setUser(profileData.data);
            setServiceId(profileData.data.serviceId || '');
            setSkills(Array.isArray(profileData.data.skills) ? profileData.data.skills : []);
            setProviderForm(buildProviderForm(profileData.data));
            mergeStoredUser(profileData.data);
          } else if (profileData === null && u.email) {
            try {
              const fallback = await api.getProfileByEmail(u.email);
              if (fallback && fallback.success && fallback.data) {
                setUser(fallback.data);
                setServiceId(fallback.data.serviceId || '');
                setSkills(Array.isArray(fallback.data.skills) ? fallback.data.skills : []);
                setProviderForm(buildProviderForm(fallback.data));
                mergeStoredUser(fallback.data);
              }
            } catch {
              // ignore fallback errors
            }
          }
        }
      })
      .finally(() => setLoading(false));
  }, [navigate]);

  const selectedServiceTitle = useMemo(() => {
    const selected = String(serviceId || '').trim();
    if (!selected) return '';
    const s = services.find((x) => String(x?._id || '') === selected) || services.find((x) => String(x?.title || '').trim() === selected);
    return s?.title || '';
  }, [serviceId, services]);

  const normalizeSkill = (s) => String(s || '').trim().replace(/\s+/g, ' ');

  const addSkill = () => {
    const next = normalizeSkill(skillInput);
    if (!next) return;
    setSkills((prev) => {
      const current = Array.isArray(prev) ? prev : [];
      if (current.some((x) => normalizeSkill(x).toLowerCase() === next.toLowerCase())) return current;
      return [...current, next];
    });
    setSkillInput('');
  };

  const removeSkill = (skill) => {
    setSkills((prev) => (Array.isArray(prev) ? prev.filter((s) => s !== skill) : []));
  };

  const readFileAsDataUrl = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const filesToDataUrls = async (files) => {
    const list = Array.isArray(files) ? files : [];
    const out = [];
    for (const file of list) {
      try {
        out.push(await readFileAsDataUrl(file));
      } catch {
        // ignore one file errors
      }
    }
    return out;
  };

  const handleSave = async () => {
    if (!user) return;
    const userId = user.id || user._id;
    if (!userId) return;
    setSaving(true);
    setSaveMessage('');
    try {
      const [photoUploads, videoUploads, projectUploads] = await Promise.all([
        portfolioPhotoFiles.length ? filesToDataUrls(portfolioPhotoFiles) : Promise.resolve([]),
        portfolioVideoFiles.length ? filesToDataUrls(portfolioVideoFiles) : Promise.resolve([]),
        portfolioProjectFiles.length ? filesToDataUrls(portfolioProjectFiles) : Promise.resolve([]),
      ]);

      const payload = {
        serviceId: serviceId || undefined,
        skills,
        ...providerForm,
        ...(photoUploads.length ? { portfolioPhotos: photoUploads } : {}),
        ...(projectUploads.length ? { portfolioProjects: projectUploads } : {}),
        ...(videoUploads.length ? { portfolioVideos: videoUploads } : {}),
      };
      const res = await api.updateProfile(userId, payload);
      if (res && res.success && res.data) {
        const updated = res.data;
        setUser(updated);
        mergeStoredUser(updated);
        setSaveMessage(t('providerSettings.saved'));
        setIsEditing(false);
        setPortfolioPhotoFiles([]);
        setPortfolioVideoFiles([]);
        setPortfolioProjectFiles([]);
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  const handleStartEdit = () => {
    setSaveMessage('');
    setProviderForm(buildProviderForm(user));
    setPortfolioPhotoFiles([]);
    setPortfolioVideoFiles([]);
    setPortfolioProjectFiles([]);
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setServiceId(user?.serviceId || '');
    setSkills(Array.isArray(user?.skills) ? user.skills : []);
    setProviderForm(buildProviderForm(user));
    setSkillInput('');
    setSaveMessage('');
    setPortfolioPhotoFiles([]);
    setPortfolioVideoFiles([]);
    setPortfolioProjectFiles([]);
    setIsEditing(false);
  };
  const handleFieldChange = (key, value) => {
    setProviderForm((prev) => {
      if (key === 'region') return { ...prev, region: value, city: '' };
      if (key === 'sector') return { ...prev, sector: value, subSector: '' };
      return { ...prev, [key]: value };
    });
  };

  const hasValue = (value) => {
    if (value == null) return false;
    if (typeof value === 'string') return value.trim().length > 0;
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === 'object') return Object.keys(value).length > 0;
    return true;
  };

  const displayValue = (value) => {
    if (!hasValue(value)) return '—';
    if (Array.isArray(value)) return value.join(', ');
    if (typeof value === 'object') {
      try {
        return JSON.stringify(value);
      } catch {
        return String(value);
      }
    }
    return String(value);
  };

  const zoneIntervention =
    user?.coverageGuarantees ||
    user?.location ||
    [user?.city, user?.region].filter(Boolean).join(', ');

  /** Récapitulatif : uniquement la prestation (pas l’identité / coordonnées — voir Profil). */
  const providerRowsPrestation = [
    [t('providerSettings.rows.trade'), selectedServiceTitle || user?.serviceId],
    [t('providerSettings.rows.sector'), user?.sector],
    [t('providerSettings.rows.subSector'), user?.subSector],
    [t('providerSettings.rows.skills'), skills],
    [t('providerSettings.rows.experience'), user?.experience],
    [t('providerSettings.rows.qualifications'), user?.qualifications],
    [t('providerSettings.rows.description'), user?.description],
    [t('providerSettings.rows.zone'), zoneIntervention],
    [t('providerSettings.rows.pricing'), user?.pricing],
    [t('providerSettings.rows.schedule'), user?.schedule],
  ];

  if (loading) {
    return (
      <AppLayout title={t('providerSettings.pageTitle')} rootClass="layout-1">
        <NioSection className="pt-120 pt-lg-160">
          <Row className="justify-content-center">
            <Col lg={8}>
              <NioCard className="shadow-sm">
                <NioCard.Body className="p-5 text-center text-muted">
                  {t('providerSettings.loading')}
                </NioCard.Body>
              </NioCard>
            </Col>
          </Row>
        </NioSection>
      </AppLayout>
    );
  }

  return (
    <AppLayout title={t('providerSettings.pageTitle')} rootClass="layout-1">
      <NioSection className="pt-120 pt-lg-160">
        <Row className="justify-content-center">
          <Col lg={8}>
            <NioCard className="shadow-sm">
              <NioCard.Body className="p-4 p-lg-5">
                <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4">
                  <div>
                    <h2 className="mb-1">{t('providerSettings.heading')}</h2>
                    <p className="text-muted mb-0">
                      {t('providerSettings.subheading')}
                    </p>
                  </div>
                  <div className="d-flex gap-2">
                    <NioButton href="/profile" label={t('providerSettings.back')} className="btn-outline-secondary btn-sm" />
                    {!isEditing && (
                      <NioButton
                        onClick={handleStartEdit}
                        label={t('providerSettings.edit')}
                        className="btn-outline-primary btn-sm"
                        disabled={saving}
                      />
                    )}
                    {isEditing && (
                      <>
                        <NioButton
                          onClick={handleCancelEdit}
                          label={t('providerSettings.cancel')}
                          className="btn-outline-secondary btn-sm"
                          disabled={saving}
                        />
                        <NioButton
                          onClick={handleSave}
                          label={saving ? t('providerSettings.saving') : t('providerSettings.save')}
                          className="btn-primary btn-sm"
                          disabled={saving}
                        />
                      </>
                    )}
                  </div>
                </div>
                {saveMessage && (
                  <div className="alert alert-success py-2 mb-4" role="alert">
                    {saveMessage}
                  </div>
                )}

                {isEditing && (
                  <Row className="gy-4 mb-3">
                    <Col xs={12}>
                      <Form.Label className="text-muted small text-uppercase">{t('providerSettings.serviceTrade')}</Form.Label>
                      <Form.Select
                        value={serviceId}
                        onChange={(e) => setServiceId(e.target.value)}
                        disabled={saving}
                      >
                        <option value="">{t('providerSettings.selectService')}</option>
                        {services.map((s) => (
                          <option key={s._id || s.title} value={s._id || s.title}>
                            {s.title}
                          </option>
                        ))}
                      </Form.Select>
                    </Col>
                    <Col md={6}>
                      <Form.Label className="text-muted small text-uppercase">{t('providerSettings.sector')}</Form.Label>
                      <NioField.Select
                        id="providerSector"
                        value={providerForm.sector}
                        onChange={(e) => handleFieldChange('sector', e.target.value)}
                        disabled={saving}
                      >
                        <option value="">{t('providerSettings.choose')}</option>
                        {sectorOptions.map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </NioField.Select>
                    </Col>
                    <Col md={6}>
                      <Form.Label className="text-muted small text-uppercase">{t('providerSettings.subSector')}</Form.Label>
                      <NioField.Select
                        id="providerSubSector"
                        value={providerForm.subSector}
                        onChange={(e) => handleFieldChange('subSector', e.target.value)}
                        disabled={saving || !providerForm.sector || subSectorOptions.length === 0}
                      >
                        <option value="">{t('providerSettings.choose')}</option>
                        {subSectorOptions.map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </NioField.Select>
                    </Col>
                    <Col md={6}>
                      <Form.Label className="text-muted small text-uppercase">{t('providerSettings.regionZone')}</Form.Label>
                      <NioField.Select
                        id="providerRegion"
                        value={providerForm.region}
                        onChange={(e) => handleFieldChange('region', e.target.value)}
                        disabled={saving}
                      >
                        <option value="">{t('providerSettings.choose')}</option>
                        {sortedRegions.map((r) => (
                          <option key={r} value={r}>
                            {r}
                          </option>
                        ))}
                      </NioField.Select>
                    </Col>
                    <Col md={6}>
                      <Form.Label className="text-muted small text-uppercase">{t('providerSettings.cityZone')}</Form.Label>
                      <NioField.Select
                        id="providerCity"
                        value={providerForm.city}
                        onChange={(e) => handleFieldChange('city', e.target.value)}
                        disabled={saving || !providerForm.region}
                      >
                        <option value="">{t('providerSettings.choose')}</option>
                        {availableProviderCities.map((c) => (
                          <option key={c} value={c}>
                            {c}
                          </option>
                        ))}
                      </NioField.Select>
                    </Col>
                    <Col xs={12}>
                      <Form.Label className="text-muted small text-uppercase">{t('providerSettings.skills')}</Form.Label>
                      <div className="d-flex gap-2">
                        <Form.Control
                          value={skillInput}
                          onChange={(e) => setSkillInput(e.target.value)}
                          placeholder={t('providerSettings.skillsPlaceholder')}
                          disabled={saving}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              addSkill();
                            }
                          }}
                        />
                        <NioButton
                          onClick={addSkill}
                          label={t('providerSettings.addSkill')}
                          className="btn-outline-primary"
                          disabled={saving}
                        />
                      </div>
                      {skills.length > 0 && (
                        <div className="mt-3 d-flex flex-wrap gap-2">
                          {skills.map((s) => (
                            <button
                              key={s}
                              type="button"
                              className="btn btn-sm btn-outline-secondary"
                              onClick={() => removeSkill(s)}
                              disabled={saving}
                              title={t('providerSettings.removeSkill')}
                            >
                              {s} <span className="ms-1">×</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </Col>
                    <Col md={6}>
                      <Form.Label className="text-muted small text-uppercase">{t('providerSettings.experience')}</Form.Label>
                      <Form.Control value={providerForm.experience} onChange={(e) => handleFieldChange('experience', e.target.value)} disabled={saving} />
                    </Col>
                    <Col md={12}>
                      <Form.Label className="text-muted small text-uppercase">{t('providerSettings.qualifications')}</Form.Label>
                      <Form.Control as="textarea" rows={2} value={providerForm.qualifications} onChange={(e) => handleFieldChange('qualifications', e.target.value)} disabled={saving} />
                    </Col>
                    <Col md={12}>
                      <Form.Label className="text-muted small text-uppercase">{t('providerSettings.description')}</Form.Label>
                      <Form.Control as="textarea" rows={2} value={providerForm.description} onChange={(e) => handleFieldChange('description', e.target.value)} disabled={saving} />
                    </Col>
                    <Col md={6}>
                      <Form.Label className="text-muted small text-uppercase">{t('providerSettings.pricing')}</Form.Label>
                      <Form.Control value={providerForm.pricing} onChange={(e) => handleFieldChange('pricing', e.target.value)} disabled={saving} />
                    </Col>
                    <Col md={12}>
                      <Form.Label className="text-muted small text-uppercase">{t('providerSettings.zoneDetail')}</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        value={providerForm.coverageGuarantees}
                        onChange={(e) => handleFieldChange('coverageGuarantees', e.target.value)}
                        disabled={saving}
                        placeholder={t('providerSettings.zonePlaceholder')}
                      />
                    </Col>
                    <Col md={12}>
                      <Form.Label className="text-muted small text-uppercase">{t('providerSettings.schedule')}</Form.Label>
                      <Form.Control as="textarea" rows={2} value={providerForm.schedule} onChange={(e) => handleFieldChange('schedule', e.target.value)} disabled={saving} />
                    </Col>
                  </Row>
                )}

                {!isEditing && (
                  <Col xs={12}>
                    <hr className="my-1" />
                    <h5 className="mt-3 mb-3">{t('providerSettings.prestationInfo')}</h5>
                    <p className="text-muted small mb-3">
                      {t('providerSettings.prestationNote')}{' '}
                      <Link to="/profile" className="text-primary">
                        {t('providerSettings.profile')}
                      </Link>.
                    </p>
                    <Row className="gy-3">
                      {(() => {
                        const alwaysShowPrestation = new Set([
                          t('providerSettings.rows.schedule'),
                          t('providerSettings.rows.zone'),
                          t('providerSettings.rows.experience'),
                          t('providerSettings.rows.pricing'),
                          t('providerSettings.rows.trade'),
                          t('providerSettings.rows.sector'),
                          t('providerSettings.rows.subSector'),
                        ]);

                        return providerRowsPrestation
                          .filter(([label, value]) => hasValue(value) || alwaysShowPrestation.has(label))
                          .map(([label, value]) => (
                            <Col xs={12} md={6} key={label}>
                              <div className="border-bottom border-light pb-2">
                                <div className="text-muted small text-uppercase">{label}</div>
                                <div>{displayValue(value)}</div>
                              </div>
                            </Col>
                          ));
                      })()}
                    </Row>
                  </Col>
                )}
              
              </NioCard.Body>
            </NioCard>
          </Col>
        </Row>
      </NioSection>
    </AppLayout>
  );
}

